import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Gift, Sparkles } from "lucide-react";
import { useAccess } from "../context/AccessContext";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";

/**
 * FoundersRewardPopup — one-time celebration shown to every user who
 * received the Jun 2026 Founder's Reward campaign (+20 days of premium).
 *
 * Display gating:
 *   • `access.founders_reward_pending === true` (server-side)
 *   • The popup is dismissed via POST /api/founders-reward/dismiss, which
 *     stamps `founders_reward_popup_seen = true`. Refreshing /me/access
 *     then surfaces `founders_reward_pending = false` and prevents re-display
 *     across devices.
 *
 * Architecture note: open state is DERIVED from access + dismissed flag —
 * no setState inside useEffect. This keeps the React Compiler happy and
 * eliminates the entire class of "popup reopens itself in a loop" bugs.
 *
 * Display priority:
 *   • Higher than WelcomeTrialPopup. When this is open WelcomeTrialPopup
 *     stays closed thanks to its own `founders_reward_pending` guard.
 */
export default function FoundersRewardPopup() {
  const { access, refresh } = useAccess();
  const [dismissedLocal, setDismissedLocal] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Pure derived flag — no effect-set-state risk.
  const shouldShow = !!access?.founders_reward_pending && !dismissedLocal;

  // Side-effect-only: fire analytics on first render where we go open.
  useEffect(() => {
    if (!shouldShow) return;
    trackEvent(EVENTS.FOUNDERS_REWARD_POPUP_SHOWN, {
      reward_days: access?.founders_reward_days || 20,
    });
  }, [shouldShow, access?.founders_reward_days]);

  const close = async () => {
    if (dismissing) return;
    setDismissing(true);
    setDismissedLocal(true);
    try {
      await api.post("/founders-reward/dismiss");
      trackEvent(EVENTS.FOUNDERS_REWARD_POPUP_DISMISSED);
      if (refresh) refresh();
    } catch {
      // Network error — the popup will reappear on next refresh, which is
      // fine. The user got their reward regardless.
    } finally {
      setDismissing(false);
    }
  };

  return (
    <Dialog open={shouldShow} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-3xl border-amber-500/30 bg-white p-0 shadow-2xl shadow-amber-500/10 dark:bg-zinc-950"
        data-testid="founders-reward-popup"
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 px-6 py-7 text-white">
          <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/15 blur-2xl" aria-hidden="true"/>
          <div className="pointer-events-none absolute -left-8 -bottom-12 h-32 w-32 rounded-full bg-white/10 blur-2xl" aria-hidden="true"/>
          <div className="relative flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm">
              <Gift size={22}/>
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-bold text-white">
                🎁 Founder&apos;s Reward
              </DialogTitle>
              <p className="text-xs text-amber-50/90">+{access?.founders_reward_days || 20} days of Premium, on us</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <DialogDescription className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            Thank you for being one of the earliest Syllox users.
          </DialogDescription>
          <p className="mt-2.5 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            As a token of appreciation, we&apos;ve extended your Premium Access by an additional <b className="text-amber-600 dark:text-amber-400">{access?.founders_reward_days || 20} days</b>.
          </p>
          <p className="mt-2.5 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            Enjoy all Premium features and continue your preparation journey with Syllox.
          </p>

          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              onClick={close}
              disabled={dismissing}
              data-testid="founders-reward-dismiss"
              className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 text-white shadow-md shadow-amber-500/30 hover:from-amber-600 hover:to-orange-600"
            >
              <Sparkles size={14} className="mr-1.5"/>
              {dismissing ? "…" : "Awesome 🚀"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
